import { useState } from 'react';
import { Link, Tooltip, useMediaQuery } from '@mui/material';
import { Datagrid, FunctionField, List, SelectInput, SimpleList, TextInput } from 'react-admin';
import moment from 'moment';
import MyPagination from '../MyPagination';
import FilteredLogsDialog from './FilteredLogsDialog';
import { CATEGORY_CHOICES, circleLogSummary, logActionText, platformText } from './activityLogLabels';
import { actionLabel, isCircleAction } from '../SupportCircles/circleLogLabels';

type Filter = { userId?: string; companyId?: string; action?: string; actionCategory?: string; entityId?: string };

// GH-841: category + entity filters (a support circle's _id = the circle's whole log)
const LOG_FILTERS = [
	<SelectInput key="actionCategory" source="actionCategory" label="קטגוריה" choices={CATEGORY_CHOICES} alwaysOn />,
	<TextInput key="entityId" source="entityId" label="מזהה ישות (למשל מעגל)" />,
];

const ActivityLogsList = () => {
	const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
	const [dialogOpen, setDialogOpen] = useState(false);
	const [filter, setFilter] = useState<Filter>({});
	const [filterLabel, setFilterLabel] = useState('');

	const openFilterDialog = (type: 'user' | 'company' | 'action', record: any) => {
		if (type === 'user' && record.userId?._id) {
			setFilter({ userId: record.userId._id });
			setFilterLabel(record.userName ?? 'משתמש');
		} else if (type === 'company' && record.companyId?._id) {
			setFilter({ companyId: record.companyId._id });
			setFilterLabel(record.companyName ?? 'חברה');
		} else if (type === 'action' && record.action) {
			setFilter({ action: record.action });
			setFilterLabel(isCircleAction(record.action) ? `${actionLabel(record.action)} (${record.action})` : record.action);
		} else return;
		setDialogOpen(true);
	};

	const cellLink = (type: 'user' | 'company' | 'action', record: any, text: string) => {
		const canOpen =
			(type === 'user' && record.userId?._id) ||
			(type === 'company' && record.companyId?._id) ||
			(type === 'action' && record.action);
		if (!canOpen || !text) return text || '-';
		return (
			<Link
				component="button"
				variant="body2"
				onClick={(e) => {
					e.stopPropagation();
					openFilterDialog(type, record);
				}}
				sx={{ cursor: 'pointer', textAlign: 'right' }}
			>
				{text}
			</Link>
		);
	};

	return (
		<>
			<List
				resource="activity-logs"
				perPage={20}
				pagination={<MyPagination />}
				sort={{ field: 'timestamp', order: 'DESC' }}
				title="לוג פעילות"
				filters={LOG_FILTERS}
			>
				{isSmall ? (
					<SimpleList
						primaryText={(record) => record.userName ?? (logActionText(record) || '-')}
						secondaryText={(record) =>
							record.companyName
								? `${record.companyName} • ${logActionText(record)}`
								: record.timestamp
									? moment(record.timestamp).format('DD/MM/YYYY HH:mm')
									: ''
						}
						tertiaryText={(record) =>
							record.timestamp ? moment(record.timestamp).format('DD/MM/YYYY HH:mm') : ''
						}
					/>
				) : (
					<Datagrid rowClick={false} bulkActionButtons={false}>
						<FunctionField
							label="משתמש"
							textAlign="right"
							render={(record: any) => cellLink('user', record, record.userName ?? '')
							}
						/>
						<FunctionField
							label="חברה"
							textAlign="right"
							render={(record: any) => cellLink('company', record, record.companyName ?? '')
							}
						/>
						<FunctionField
							label="פעולה"
							textAlign="right"
							render={(record: any) =>
								isCircleAction(record.action) ? (
									<Tooltip title={record.action}>
										<span>{cellLink('action', record, logActionText(record))}</span>
									</Tooltip>
								) : (
									cellLink('action', record, record.action ?? '')
								)
							}
						/>
						<FunctionField
							label="תאריך"
							textAlign="center"
							render={(record: any) =>
								record.timestamp
									? moment(record.timestamp).format('DD/MM/YYYY HH:mm')
									: '-'
							}
						/>
						<FunctionField
							label="פלטפורמה"
							textAlign="right"
							render={(record: any) => platformText(record.platform)}
						/>
						<FunctionField
							label="מטא-דאטה"
							textAlign="right"
							render={(record: any) => {
								// GH-841: a support-circle row reads as one Hebrew line; the full JSON is in the tooltip
								const circleLine = circleLogSummary(record);
								if (circleLine) {
									let json = '';
									try {
										json = JSON.stringify(record.metadata ?? {});
									} catch {
										json = '';
									}
									return (
										<Tooltip title={json.length > 1500 ? `${json.slice(0, 1500)}…` : json}>
											<span>{circleLine.length > 100 ? `${circleLine.slice(0, 100)}…` : circleLine}</span>
										</Tooltip>
									);
								}
								if (record.metadata && typeof record.metadata === 'object' && Object.keys(record.metadata).length > 0) {
									try {
										const str = JSON.stringify(record.metadata);
										return str.length > 80 ? str.slice(0, 80) + '…' : str;
									} catch {
										return '-';
									}
								}
								if (record.entityId) {
									return record.entityType ? `${record.entityType}: ${record.entityId}` : record.entityId;
								}
								return '-';
							}}
						/>
					</Datagrid>
				)}
			</List>
			<FilteredLogsDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				filter={filter}
				filterLabel={filterLabel}
			/>
		</>
	);
};

export default ActivityLogsList;
